import { color, PastelName } from "./tokens";
import type { IconName } from "./icons";

/**
 * Editorial page data — the single source of truth for the v2 site.
 * Dates corrected against LinkedIn (2026-08): Zip internship/contract
 * 2018–2020, Palo IT 2020–2023, Zip return Jan–May 2024, 4Mation
 * Sep 2024–Jul 2025, Palo IT senior Jul 2025–present.
 */

export type EditorialProject = {
  id: string;
  title: string;
  client: string;
  brand: string;
  icon: IconName;
  bullets: string[];
  stack: string[];
  /** Serial year when the project started later than its era. */
  year?: string;
};

export type CardData = {
  project: EditorialProject;
  serial: string;
  pastel: string;
  pastelName: PastelName;
};

export type EraSection = {
  number: string;
  code: string;
  title: string;
  years: string;
  startYear: string;
  icon: IconName;
  cards: CardData[];
};

export const CONTACT = {
  // TODO(elbe): confirm the public contact email + LinkedIn URL
  email: "pibatsolutions@gmail.com",
  linkedIn: "https://www.linkedin.com/in/elbewan",
};

export const STATS = [
  { value: "8", label: "years" },
  { value: "14+", label: "companies" },
  { value: "5", label: "industries" },
  { value: "15+", label: "products shipped" },
];

const PASTEL_CYCLE: PastelName[] = ["lavender", "blush", "sand", "sage", "sky"];

type EraDef = {
  id: string;
  code: string;
  title: string;
  years: string;
  startYear: string;
  icon: IconName;
  projects: EditorialProject[];
};

const ERA_DEFS: EraDef[] = [
  {
    id: "zip",
    code: "ZIP",
    title: "Zip Co",
    years: "2018–2020",
    startYear: "2018",
    icon: "cart",
    projects: [
      {
        id: "zip-contentful",
        title: "Contentful CMS Engine",
        client: "Zip Co — Frontend Engineer",
        brand: "#6E2FEB",
        icon: "cart",
        bullets: [
          "Dynamic marketing pages alongside Amazon, Kmart and Service NSW",
          "Contentful lead & trainer — build scripts that saved designers hours per week",
        ],
        stack: ["React", "Contentful", "AWS Lambda", "GTM"],
      },
    ],
  },
  {
    id: "paloEngineer",
    code: "PALO",
    title: "Palo IT",
    years: "2020–2023",
    startYear: "2020",
    icon: "layers",
    projects: [
      {
        id: "bookingsg",
        title: "BookingSG V1 & RBS V2",
        client: "GovTech Singapore",
        brand: "#EF3340",
        icon: "calendar",
        bullets: [
          "Complete booking system for admins, agency-admins and users",
          "V2: resources, announcements, notifications, locations, user groups",
        ],
        stack: ["React/Typescript", "Nodejs/Typescript"],
      },
      {
        id: "firstrescue",
        title: "First Rescue",
        client: "IAG",
        brand: "#FF7A00",
        icon: "map-pin",
        bullets: [
          "Whitelabel web-app linking customers to towing services, end to end",
          "Google Maps + ServiceNow for live roadside-assistance tracking",
        ],
        stack: ["Nextjs", "Material UI", "Google Maps", "ServiceNow"],
      },
      {
        id: "hsbc",
        title: "Retail DCO/ICO",
        client: "HSBC",
        brand: "#DB0011",
        icon: "landmark",
        bullets: [
          "Uplifted the DCO/ICO onboarding journey for HSBC online accounts",
        ],
        stack: ["React", "Nodejs", "Jenkins", "SonarQube"],
      },
      {
        id: "blocktrust",
        title: "Blocktrust",
        client: "Blockchain / NFT",
        brand: "#E8B923",
        icon: "boxes",
        bullets: [
          "Refactored a white-label blockchain/NFT platform for multiple tenants",
        ],
        stack: ["Nextjs", "C# Backend"],
      },
      {
        id: "whattruck",
        title: "WhatTruck",
        client: "Botany Access",
        brand: "#2E9E5B",
        icon: "truck",
        bullets: [
          "End-to-end marketplace app + web app for heavy-load trucking",
        ],
        stack: ["React Native/Typescript", "Nodejs/Typescript"],
      },
    ],
  },
  {
    id: "zipReturn",
    code: "ZIP",
    title: "Zip Co — Return",
    years: "2024",
    startYear: "2024",
    icon: "database",
    projects: [
      {
        id: "zip-dotnet",
        title: ".NET Microservices",
        client: "Zip Co — Full Stack Engineer",
        brand: "#6E2FEB",
        icon: "database",
        bullets: [
          "Maintained microservices across .NET, Microsoft SQL, Angular and React",
          "Picked up .NET as a new stack mid-flight",
        ],
        stack: [".NET Core / C#", "Microsoft SQL", "Angular", "React"],
      },
    ],
  },
  {
    id: "fourMation",
    code: "4MTN",
    title: "4Mation",
    years: "2024–2025",
    startYear: "2024",
    icon: "bot",
    projects: [
      {
        id: "qvego",
        title: "QV / EGO AI Chatbot",
        client: "4Mation — Tech Lead",
        brand: "#C084FC",
        icon: "bot",
        bullets: [
          "Multiple Amazon Bedrock Claude agents + lambdas: recommendations, complaints, moderation",
          "Multi-agent collaboration for accuracy + latency; vector knowledge bases",
          "Deep-chat frontend + Retool dashboard to review conversations",
        ],
        stack: ["AWS Bedrock", "AWS Lambda", "React/Typescript"],
      },
      {
        id: "montgomery",
        title: "AI Email Categorizer",
        client: "Montgomery Homes — Tech Lead",
        brand: "#FDA4AF",
        icon: "mail",
        bullets: [
          "OpenAI categorization on a cronjob with a rule engine to forward, label, move",
          "Lovable frontend + Strapi backend (SSO) to review job logs",
        ],
        stack: ["OpenAI", "Strapi", "Typescript Cronjob"],
      },
      {
        id: "ida",
        title: "Industrial Deafness Australia",
        client: "4Mation — Developer",
        brand: "#7DD3FC",
        icon: "clipboard",
        bullets: [
          "Rostering calendar (custom react-big-calendar) + react-flow workflow system",
          "Custom form builder and task management solution",
        ],
        stack: ["React/Typescript", "Node/Strapi"],
      },
    ],
  },
  {
    id: "paloSenior",
    code: "SNR",
    title: "Palo IT — Senior",
    years: "2025–now",
    startYear: "2025",
    icon: "workflow",
    projects: [
      {
        id: "vista",
        title: "Vista — Agentic Orchestrator",
        client: "Palo IT — Senior Consultant",
        brand: "#8B5CF6",
        icon: "workflow",
        year: "2026",
        bullets: [
          "Extracted complex business logic into agentic workflows, rules and skills",
          "Agentic orchestrator running complex workflows with different models",
          "GitHub, Azure, Cursor, Copilot & OpenRouter integrations — tickets completed autonomously",
          "Implementation plans actioned through the orchestrator: 1980–2026 code, WinForms → Angular/C#",
        ],
        stack: ["Agentic Workflows", "OpenRouter", "Angular/C#"],
      },
      {
        id: "imed",
        title: "IMOGEN Chatbot",
        client: "I-MED — Senior Consultant",
        brand: "#14B8A6",
        icon: "chat",
        year: "2026",
        bullets: [
          "Middleware / chatbot handling AI messaging, escalating to Genesys human agents",
          "Solutioned and architected with the I-MED team — pre-delivery to go-live",
        ],
        stack: ["AI Chatbot", "Genesys", "Node/Typescript"],
      },
      {
        id: "applus",
        title: "Australian Payments Plus",
        client: "Palo IT — Senior Consultant",
        brand: "#FFD100",
        icon: "credit-card",
        bullets: [
          "Frontend (detail unavailable due to NDA)",
          "Seminars & consulting: AI (Gen-e2) to code, plan and design with quality",
        ],
        stack: ["React/Typescript", "Nodejs/Typescript", "Gen-e2"],
      },
      {
        id: "urbnsurf",
        title: "UrbnSurf — Phase 1 + 1.5",
        client: "Palo IT — Senior Consultant",
        brand: "#0EA5E9",
        icon: "waves",
        year: "2026",
        bullets: [
          "Full booking flow for guest and logged-in users with multiple flows",
          "Authentication, dashboard, memberships, multipacks and gift cards",
          "Complete CMS system for custom pages and website data",
        ],
        stack: ["React/NextJs/Typescript", "Nodejs/Typescript", "Figma Make"],
      },
      {
        id: "virginactive",
        title: "Virgin Active",
        client: "Palo IT — Senior Consultant",
        brand: "#E10A0A",
        icon: "dumbbell",
        year: "2026",
        bullets: [
          "Consulted and developed new website and features across SG, TH & AU",
          "MyLocker frontend — new product and app for gym goers",
        ],
        stack: ["React", "Typescript"],
      },
    ],
  },
];

export const ERA_SECTIONS: EraSection[] = ERA_DEFS.map((era, eraIndex) => ({
  number: String(eraIndex + 1).padStart(2, "0"),
  code: era.code,
  title: era.title,
  years: era.years,
  startYear: era.startYear,
  icon: era.icon,
  cards: era.projects.map((project, i) => ({
    project,
    serial: `${era.code}-${project.year ?? era.startYear} · ${String(i + 1).padStart(2, "0")}`,
    pastel: color.pastel[PASTEL_CYCLE[(eraIndex + i) % PASTEL_CYCLE.length]],
    pastelName: PASTEL_CYCLE[(eraIndex + i) % PASTEL_CYCLE.length],
  })),
}));

export const [
  ZIP_SECTION,
  PALO_SECTION,
  ZIP_RETURN_SECTION,
  FOURMATION_SECTION,
  SENIOR_SECTION,
] = ERA_SECTIONS;

export const STACK_CLUSTERS: {
  label: string;
  icon: IconName;
  chips: string[];
}[] = [
  {
    label: "TypeScript",
    icon: "braces",
    chips: ["Typescript", "Node/Typescript", ".NET Core / C#", "Angular/C#"],
  },
  {
    label: "React",
    icon: "atom",
    chips: [
      "React",
      "Nextjs",
      "React Native",
      "Material UI",
      "styled-components",
    ],
  },
  {
    label: "Node",
    icon: "server",
    chips: ["Nodejs", "Strapi", "MySQL", "ServiceNow", "Contentful"],
  },
  {
    label: "AI",
    icon: "brain",
    chips: [
      "AWS Bedrock",
      "OpenAI",
      "OpenRouter",
      "Agentic Workflows",
      "Gen-e2",
      "Genesys",
    ],
  },
  {
    label: "Cloud",
    icon: "cloud",
    chips: ["AWS Lambda", "Azure", "Jenkins", "SonarQube", "Dynatrace"],
  },
];
